<script setup>
import {ref, computed, onMounted} from 'vue'
import helpers from '@/lib/helpers'

const props = defineProps({
  // the record's activity endpoint, e.g. /api/customer/12/activity
  url: {type: String, required: true},
  // permission needed to log / delete
  write: {type: String, default: null},
  // explicit override when the caller already resolved the permission (wins over `write`)
  writeAllowed: {type: Boolean, default: null},
  height: {type: String, default: '360px'},
})

const rows = ref([])
const more = ref(false)
const nextBefore = ref(null)
const loading = ref(false)
const adding = ref(false)

const canWrite = computed(() =>
    props.writeAllowed !== null ? props.writeAllowed : (!props.write || helpers.can(props.write)))

const TYPES = [
  {value: 'call', text: 'Call'},
  {value: 'email', text: 'Email'},
  {value: 'meeting', text: 'Meeting'},
  {value: 'text', text: 'Text'},
  {value: 'note', text: 'Note'},
]

const ICON = {
  call: ['fas', 'phone'], email: ['far', 'envelope'], reply: ['fas', 'reply'],
  meeting: ['fas', 'users'], text: ['fas', 'comment-sms'], mailing: ['fas', 'paper-plane'],
  note: ['far', 'note-sticky'], stage_change: ['fas', 'right-left'], link: ['fas', 'link'],
  task: ['fas', 'square-check'], order: ['fas', 'file-invoice-dollar'],
  error: ['fas', 'triangle-exclamation'], system: ['fas', 'gear'],
}
const iconFor = (t) => ICON[t] || ['far', 'circle']

const form = ref({type: 'call', direction: 'out', summary: '', outcome: '', body: '', occurred_at: ''})
const resetForm = () => (form.value = {type: 'call', direction: 'out', summary: '', outcome: '', body: '', occurred_at: ''})

const load = async (append = false) => {
  loading.value = true
  try {
    const params = {limit: 30}
    if (append && nextBefore.value) params.before = nextBefore.value
    const res = await helpers.asyncAction(props.url, 'GET', {noload: true, params})
    rows.value = append ? [...rows.value, ...(res.data || [])] : (res.data || [])
    more.value = !!res.more
    nextBefore.value = res.next_before
  } finally {
    loading.value = false
  }
}

onMounted(() => load())

const save = async () => {
  if (!form.value.summary.trim()) return
  const created = await helpers.asyncAction(props.url, 'POST', {noload: true, data: {...form.value}})
  rows.value = [created, ...rows.value]
  adding.value = false
  resetForm()
  helpers.toast('Activity logged')
}

const remove = async (row) => {
  if (!row._links || !await helpers.confirm('Delete this activity?')) return
  await helpers.asyncAction(row._links.delete_url.href, row._links.delete_url.method, {noload: true})
  rows.value = rows.value.filter(r => r.id !== row.id)
}
</script>

<template>
  <div>
    <div class="d-flex align-items-center mb-2">
      <b-button v-if="canWrite && !adding" size="sm" variant="outline-primary" @click="adding = true">
        <font-awesome-icon :icon="['fas', 'plus']" class="mr-1"/>Log activity
      </b-button>
      <b-spinner v-if="loading" small class="ml-2 text-muted"/>
      <span class="ml-auto text-muted small">{{ rows.length }}{{ more ? '+' : '' }}</span>
    </div>

    <b-card v-if="adding" class="mb-2" body-class="p-2">
      <div class="d-flex flex-wrap" style="gap:.5rem">
        <b-form-select size="sm" v-model="form.type" :options="TYPES" style="width:8rem"/>
        <b-form-select size="sm" v-model="form.direction" style="width:6rem"
                       :options="[{value:'out',text:'Outbound'},{value:'in',text:'Inbound'},{value:'internal',text:'—'}]"/>
        <b-form-datepicker size="sm" v-model="form.occurred_at" placeholder="Now" reset-button style="width:11rem"/>
      </div>
      <b-form-input size="sm" class="mt-2" v-model="form.summary" placeholder="What happened (required)" @keydown.enter="save"/>
      <div class="d-flex mt-2" style="gap:.5rem">
        <b-form-input size="sm" v-model="form.outcome" placeholder="Outcome (e.g. left_voicemail)"/>
        <b-button size="sm" variant="primary" :disabled="!form.summary.trim()" @click="save">Save</b-button>
        <b-button size="sm" variant="link" class="text-muted" @click="adding = false; resetForm()">Cancel</b-button>
      </div>
      <b-form-textarea size="sm" class="mt-2" rows="2" v-model="form.body" placeholder="Notes (optional)"/>
    </b-card>

    <div class="overflow-auto pr-1" :style="{maxHeight: height}">
      <div v-if="!rows.length && !loading" class="text-muted small py-2">No activity yet.</div>

      <div v-for="row in rows" :key="row.id" class="d-flex py-2 border-bottom act-row"
           :class="{'text-danger': row.type === 'error'}">
        <div class="text-center mr-2" :class="row.type === 'error' ? 'text-danger' : 'text-muted'" style="width:1.4rem">
          <font-awesome-icon :icon="iconFor(row.type)"/>
        </div>
        <div class="flex-fill">
          <div class="d-flex align-items-baseline">
            <span class="font-weight-bold small mr-1">{{ row.type_desc }}</span>
            <font-awesome-icon v-if="row.direction === 'in'" :icon="['fas', 'arrow-down']" class="text-success small mr-1"/>
            <font-awesome-icon v-else-if="row.direction === 'out'" :icon="['fas', 'arrow-up']" class="text-primary small mr-1"/>
            <b-badge v-if="row.outcome" variant="light" class="mr-1">{{ row.outcome }}</b-badge>
            <b-badge v-if="row.origin !== 'manual'" variant="light" class="text-muted mr-1">{{ row.origin }}</b-badge>
            <span class="ml-auto text-muted small" v-b-tooltip.hover :title="row.occurred_at">{{ row.occurred_at | timediff }}</span>
          </div>
          <div class="small">{{ row.summary }}</div>
          <div v-if="row.body" class="small text-muted" style="white-space:pre-wrap">{{ row.body }}</div>
          <div class="small text-muted">
            <span v-if="row.user">{{ row.user }}</span>
            <b-link v-if="row.fkmessage" class="ml-2" @click="global_lookup((row.meta && row.meta.message_gli) || ('16-' + row.fkmessage), true)">open message</b-link>
            <b-link v-if="row.editable && row._links" class="ml-2 text-danger" @click="remove(row)">delete</b-link>
          </div>
        </div>
      </div>
    </div>

    <b-button v-if="more" size="sm" variant="link" block :disabled="loading" @click="load(true)">Load more</b-button>
  </div>
</template>

<style scoped>
.act-row:last-child {
  border-bottom: 0 !important;
}
</style>
